import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HiddenAnnotationsToastContainer } from '../../../containers/HiddenAnnotationsToast.container';
import { Image } from '../../../modules/annotation/components/Image';
import { useAnnotationPermissions } from '../annotation.permissions';
import { IAnnotationCreationService } from '../annotationCreation.service';
import { IAnnotationmagesQueueService } from '../annotationImagesQueue.service';
import { IAnnotationsStore } from '../annotations.store';
import { IAnnotationUiStore } from '../annotationUi.store';
import { AnnotationMoreOptions } from '../components/AnnotationMoreOptions';
import { AnnotationTopBarItem } from '../components/AnnotationTopBarItem';
import { AnnotationViewOptions } from '../components/AnnotationViewOptions';
import { NoImagePlaceholder } from '../components/NoImagePlaceholder';
import { AnnotationTypesContainer } from '../submodules/annotationTypes/containers/AnnotationTypes.container';
import { LeftMenuContainer } from '../submodules/segmentations/containers/LeftMenu.container';
import { ImageSegmentationsContextProvider } from '../submodules/segmentations/segmentationContextMenu/imageSegmentations.context';
import { AnnotationImageNameHeaderContainer } from './AnnotationImageNameHeader.container';
import { AnnotationProgressContainer } from './AnnotationProgress.container';
import { AnnotationStatusInfoContainer } from './AnnotationStatusInfoContainer';
import { BackToProjectsButtonWithDraftContainer } from './BackToProjectsButtonWithDraftContainer';
import { CreateControlsContainer } from './CreateControls.container';
import { DrawingContainer } from './Drawing.container';
import { ImageContainer } from './Image.container';
import { QuestionsContainer } from './Questions.container';
import { ReturnToToolsContainer } from './ReturnToTools.container';
import { SegmentationsContainer } from './Segmentations.container';
import { SegmentationsSaveContainer } from './SegmentationsSave.container';
import { UndoRedoAnnotationsShortcutsContainer } from './UndoRedoAnnotationsShortcuts.container';
import { ViewAttributesContainer } from './ViewAttributesContainer';

export const CreateAnnotationContainer = observer((): ReactElement => {
  const { workspaceId, projectId } = useParams<{ projectId: string; workspaceId: string }>();
  const annotationUiStore = useInjection(IAnnotationUiStore.$);
  const annotationCreationService = useInjection(IAnnotationCreationService.$);
  const imagesQueueService = useInjection(IAnnotationmagesQueueService.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);
  const annotationPermissions = useAnnotationPermissions();

  useEffect(() => {
    void setupService();
    return (): void => {
      annotationCreationService.dispose();
    };
  }, []);

  const setupService = async (): Promise<void> => {
    imagesQueueService.setup(projectId);
    await annotationCreationService.setupAsync(projectId, imagesQueueService);
  };

  const handleRefresh = async (): Promise<void> => {
    await setupService();
  };

  const noImage = annotationsStore.image === undefined && !annotationUiStore.isImageLoading && !imagesQueueService.isAnyRequestInProgress;
  const noImageReason = annotationsStore.noImageReason;
  const canReview = annotationPermissions.canReview(annotationsStore.projectId);

  return (
    <>
      <ImageSegmentationsContextProvider imageId={annotationsStore.image?.id}>
        <div className='annotation-view-top-bar'>
          <AnnotationTopBarItem>
            <BackToProjectsButtonWithDraftContainer />
          </AnnotationTopBarItem>
          <AnnotationTopBarItem>
            <UndoRedoAnnotationsShortcutsContainer undo={true} />
          </AnnotationTopBarItem>
          <AnnotationTopBarItem>
            <UndoRedoAnnotationsShortcutsContainer undo={false} />
          </AnnotationTopBarItem>
          <AnnotationTopBarItem>
            <AnnotationViewOptions disableCursorConfig={false} />
          </AnnotationTopBarItem>
          <AnnotationTopBarItem>
            <ViewAttributesContainer />
          </AnnotationTopBarItem>
          <AnnotationTopBarItem variation='image-name'>
            <AnnotationImageNameHeaderContainer />
            <AnnotationProgressContainer />
          </AnnotationTopBarItem>
          <AnnotationTopBarItem>
            <AnnotationMoreOptions
              create
              globalShortcuts={[]}
            />
          </AnnotationTopBarItem>
        </div>
        <div className='annotation-view-inner'>
          <LeftMenuContainer
            currentProjectId={projectId}
            isReadOnly={false}
            showObjectsList={true}
            isReview={false}
          />
          <HiddenAnnotationsToastContainer />
          <ImageContainer>
            {(imageProps): ReactElement => (
              <>
                {noImage ? (
                  <NoImagePlaceholder
                    canReview={canReview.allowed}
                    onRefresh={handleRefresh}
                    noImageReason={noImageReason}
                    mode={'Annotate'}
                    workspaceId={workspaceId}
                  />
                ) : (
                  <Image {...imageProps}>
                    <SegmentationsContainer />
                    <DrawingContainer disableCursorGuides={false} />
                  </Image>
                )}
              </>
            )}
          </ImageContainer>
          <div className='right-column'>
            <div className='scrollable-container'>
              <div className='scrollable-container-content'>
                <AnnotationTypesContainer>
                  <AnnotationStatusInfoContainer canChangeImageSet={false} />
                  <ReturnToToolsContainer />
                </AnnotationTypesContainer>
                <QuestionsContainer />
                <SegmentationsSaveContainer />
              </div>
            </div>
            <CreateControlsContainer />
          </div>
        </div>
      </ImageSegmentationsContextProvider>
    </>
  );
});
